import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Navbar } from '../../Shared/components/Navbar';
import { LoadingSpinner } from '../../Shared/components/LoadingSpinner';
import { Button } from '../../Shared/ui/button';
import { Card } from '../../Shared/ui/card';
import apiClient from '../../Shared/services/api';
import { extractApiError } from '../../Shared/utils/apiError';
import type { WorksheetAttempt } from '../../Shared/types';

const HistoryDetail: React.FC = () => {
  const { attemptId } = useParams<{ attemptId: string }>();
  const navigate = useNavigate();
  const [attempt, setAttempt] = useState<WorksheetAttempt | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (attemptId) {
      void loadAttemptDetail(parseInt(attemptId));
    }
  }, [attemptId]);

  const loadAttemptDetail = async (id: number): Promise<void> => {
    setIsLoading(true);
    setError('');
    try {
      const data = await apiClient.getWorksheetDetail(id);
      setAttempt(data.attempt);
    } catch (err: unknown) {
      setError(extractApiError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getScoreColor = (score: number): string => {
    if (score >= 80) {
      return 'text-green-600';
    }
    if (score >= 60) {
      return 'text-yellow-600';
    }
    return 'text-red-600';
  };

  return (
    <Navbar>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {isLoading ? (
          <LoadingSpinner />
        ) : error || !attempt ? (
          <Card>
            <p className="text-red-600 mb-4">{error || 'Worksheet not found'}</p>
            <Button onClick={() => navigate('/history')} variant="secondary">
              Back to History
            </Button>
          </Card>
        ) : (
          <div>
            {/* Header */}
            <div className="mb-8">
              <Button onClick={() => navigate('/history')} variant="secondary" className="mb-4">
                ← Back to History
              </Button>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {attempt.worksheet_type === 'diagnostic'
                  ? '🎯 Diagnostic Assessment'
                  : `📚 ${
                      typeof attempt.topic === 'object'
                        ? attempt.topic.name
                        : attempt.topic || 'Worksheet'
                    }`}
              </h1>
              {attempt.worksheet_type !== 'diagnostic' && (
                <p className="text-lg text-gray-600">
                  Pack {attempt.pack_number}, Worksheet {attempt.sequence_index}
                </p>
              )}
              <p className="text-sm text-gray-500 mt-2">
                Completed {formatDate(attempt.completed_at)}
              </p>
            </div>

            {/* Summary Stats */}
            <Card className="mb-8">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-1">Score</p>
                  <p className={`text-3xl font-bold ${getScoreColor(attempt.score_percentage)}`}>
                    {attempt.score_percentage?.toFixed(1)}%
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-1">Correct</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {attempt.correct_count} / {attempt.total_questions}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-1">Time Spent</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {Math.round((attempt.time_spent || 0) / 60)} min
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-1">Status</p>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 mt-2">
                    ✓ Completed
                  </span>
                </div>
              </div>
            </Card>

            {/* Questions Review */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Question Review</h2>
              <div className="space-y-6">
                {attempt.question_attempts?.map((qa, index) => (
                  <Card key={qa.id} className="border-l-4 border-l-gray-300">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-semibold text-gray-700">
                          Question {index + 1}
                        </span>
                        {qa.is_correct ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            ✓ Correct
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            ✗ Incorrect
                          </span>
                        )}
                      </div>
                    </div>

                    <p className="text-lg text-gray-900 mb-4">{qa.question_text}</p>

                    {qa.options && qa.options.length > 0 && (
                      <div className="mb-4 pl-4 border-l-2 border-gray-200">
                        <p className="text-sm text-gray-600 mb-2">Options:</p>
                        <ul className="space-y-1">
                          {qa.options.map((option, idx) => (
                            <li key={idx} className="text-gray-700">
                              {option}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-sm font-medium text-gray-600 mb-1">Your Answer:</p>
                        <p
                          className={`text-lg font-semibold ${
                            qa.is_correct ? 'text-green-600' : 'text-red-600'
                          }`}
                        >
                          {qa.student_answer || '(No answer)'}
                        </p>
                      </div>
                      {!qa.is_correct && (
                        <div>
                          <p className="text-sm font-medium text-gray-600 mb-1">Correct Answer:</p>
                          <p className="text-lg font-semibold text-green-600">
                            {qa.correct_answer}
                          </p>
                        </div>
                      )}
                    </div>

                    {qa.explanation && (
                      <div className="bg-blue-50 rounded-lg p-4">
                        <p className="text-sm font-medium text-blue-900 mb-2">💡 Explanation:</p>
                        <p className="text-sm text-blue-800">{qa.explanation}</p>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </Navbar>
  );
};

export default HistoryDetail;
